import { create } from "zustand";
import { fetchLogin } from "./Services/fetchlogin";
import { fetchCreateEmpleado } from "./Services/fetchCreateEmpleado";
import { fetchEmpleados as fetchEmpleadosApi } from "./Services/getEmpleados"; // <- Renombrado aquí
import { fetchUpdateEmpleado } from "./Services/fetchEditEmpleado";
import { fetchDetalleEmpleado } from "./Services/fetchDetalleEmpleado";
import { fetchPresignContrato } from "./Services/fetchPresignContrato";
import { fetchAttachContrato } from "./Services/fetchAttachContrato";
import { fetchChecador, fetchChecadorPorRango } from "./Services/Checadores/getChecadores";

const useStore = create((set, get) => ({
  // — Estado global —
  loading: false,
  loadingCreateEmpleado: false,
  loadingUpdateEmpleado: false,
  error: null,
  errorCreateEmpleado: null,
  errorUpdateEmpleado: null,
  users: [],
  nextKey: null, // <- Ahora usa nextKey, NO lastKey
  routesLoading: false,
  detalleEmpleado: null,
  loadingDetalleEmpleado: false,
  errorDetalleEmpleado: null,

  loadingChecador: false,
  errorChecador: null,
  checador: [],

  // Estado separado para el detalle de un solo empleado: no debe pisar
  // el "checador" global (usado por el calendario general, que necesita
  // las checadas de TODOS los empleados).
  loadingChecadorEmpleado: false,
  errorChecadorEmpleado: null,
  checadorEmpleado: [],
  // numeroEmpleado al que pertenecen las checadas en checadorEmpleado; null
  // mientras no hay datos confiables (cargando o con error).
  checadorEmpleadoNumero: null,
  checadorEmpleadoRequestId: 0,


  resetError: () => set({ error: null }),

  fetchDataLogin: async (body) => {
    set({ loading: true, error: null });
    try {
      await fetchLogin(body);
      set({ loading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error : new Error("Error desconocido"),
        loading: false,
      });
    }
  },

  fetchCreateEmpleado: async (body, token) => {
    set({ loadingCreateEmpleado: true, errorCreateEmpleado: null });
    try {
      const res = await fetchCreateEmpleado(body, token);
      set({ loadingCreateEmpleado: false });
      return res;
    } catch (error) {
      set({ errorCreateEmpleado: error, loadingCreateEmpleado: false });
      throw error;
    }
  },

  fetchUpdateEmpleado: async (numeroEmpleado, body, token) => {
    set({ loadingUpdateEmpleado: true, errorUpdateEmpleado: null });
    try {
      const res = await fetchUpdateEmpleado(numeroEmpleado, body, token);
      set({ loadingUpdateEmpleado: false });
      return res;
    } catch (error) {
      set({ errorUpdateEmpleado: error, loadingUpdateEmpleado: false });
      throw error;
    }
  },

  fetchPresignContrato: async (numeroEmpleado, token) => {
    try {
      const res = await fetchPresignContrato(numeroEmpleado, token);
      return res;
    } catch (error) {
      throw error;
    }
  },

  fetchAttachContrato: async (numeroEmpleado, body, token) => {
    try {
      const res = await fetchAttachContrato(numeroEmpleado, body, token);
      return res;
    } catch (error) {
      throw error;
    }
  },


  /**
   * Trae empleados con paginación.
   * @param {boolean} append Si true, agrega a la lista actual; si false, reemplaza.
   * @param {string|null} lastKey El nextKey devuelto por el backend.
   */
  fetchEmpleados: async (append = false, lastKey = null) => {
    set({ loading: true, error: null });
    try {
      const { empleados, nextKey } = await fetchEmpleadosApi(lastKey);
      set(state => ({
        users: append ? [...state.users, ...empleados] : empleados,
        nextKey,
        loading: false
      }));
    } catch (error) {
      set({ error: error, loading: false });
    }
  },

  /**
   * Trae TODAS las páginas de empleados (sigue nextKey hasta que ya no haya).
   * El backend pagina de 50 en 50, pero varias pantallas (buscador, editar,
   * calendario general) necesitan la lista completa para funcionar bien.
   */
  fetchAllEmpleados: async () => {
    set({ loading: true, error: null });
    try {
      let acumulado = [];
      let key = null;
      do {
        const { empleados, nextKey: nk } = await fetchEmpleadosApi(key);
        acumulado = acumulado.concat(empleados || []);
        key = nk || null;
      } while (key);

      set({ users: acumulado, nextKey: null, loading: false });
      return acumulado;
    } catch (error) {
      set({ error: error, loading: false });
      throw error;
    }
  },

  fetchDetalleEmpleado: async (curp, token) => {
    set({ loadingDetalleEmpleado: true, errorDetalleEmpleado: null, detalleEmpleado: null });
    try {
      const detalle = await fetchDetalleEmpleado(curp, token);
      set({ detalleEmpleado: detalle, loadingDetalleEmpleado: false });
      return detalle;
    } catch (error) {
      set({ errorDetalleEmpleado: error, loadingDetalleEmpleado: false });
      throw error;
    }
  },

  fetchChecador: async () => {
    set({ loadingChecador: true, errorChecador: null });
    try {
      const res = await fetchChecador();
      set({
        checador: res.data || [],
        loadingChecador: false,
      });
      return res;
    } catch (error) {
      set({
        errorChecador: error,
        loadingChecador: false,
      });
      throw error;
    }
  },

  // Checadas de TODOS los empleados pero solo del rango visible en el
  // calendario general (evita traer la historia completa cada vez).
  fetchChecadorPorRango: async (startDate, endDate) => {
    set({ loadingChecador: true, errorChecador: null });
    try {
      const res = await fetchChecadorPorRango(startDate, endDate);
      set({
        checador: res.data || [],
        loadingChecador: false,
      });
      return res;
    } catch (error) {
      set({
        errorChecador: error,
        loadingChecador: false,
      });
      throw error;
    }
  },

  fetchChecadorEmpleado: async (numeroEmpleado) => {
    // Cada petición tiene un id; si el usuario cambia de empleado antes de que
    // termine, la respuesta vieja se descarta y no pisa la del empleado actual.
    const requestId = get().checadorEmpleadoRequestId + 1;
    set({
      checadorEmpleadoRequestId: requestId,
      loadingChecadorEmpleado: true,
      errorChecadorEmpleado: null,
      checadorEmpleado: [],
      checadorEmpleadoNumero: null,
    });

    // El endpoint a veces tarda o falla (cold start / timeout del gateway);
    // se reintenta antes de dar el error.
    const MAX_INTENTOS = 3;
    let ultimoError;
    for (let intento = 1; intento <= MAX_INTENTOS; intento++) {
      try {
        const res = await fetchChecador(numeroEmpleado);
        if (get().checadorEmpleadoRequestId !== requestId) return res;
        set({
          checadorEmpleado: res.data || [],
          checadorEmpleadoNumero: numeroEmpleado,
          loadingChecadorEmpleado: false,
        });
        return res;
      } catch (error) {
        ultimoError = error;
        if (get().checadorEmpleadoRequestId !== requestId) return;
        if (intento < MAX_INTENTOS) {
          await new Promise((r) => setTimeout(r, 1000 * intento));
        }
      }
    }

    set({
      errorChecadorEmpleado: ultimoError,
      loadingChecadorEmpleado: false,
    });
    throw ultimoError;
  },

}));

export default useStore;
